import { TICKS_PER_BAR, TICKS_PER_BEAT } from '../../shared/tempo';
import type { ClassDefinition, UpgradeDefinition } from '../types';

export interface FoxAttack {
  kind: 'fox';
  damage: number;
  cooldownTicks: number;
  leaps: number;
  falloff: number;
  reach: number;
  range: number;
  speed: number;
  returnSpeed: number;
  roots: { leaps: number; damage: number; radius: number; perNotch: number; slowFrom: number };
}

export interface HibouEffect {
  kind: 'hibou';
  bars: number;
  damage: number;
  frozenBeats: number;
  reach: number;
  speed: number;
}

export const ENRACINE: ClassDefinition = {
  id: 'enracine',
  name: 'L’Enraciné',
  gustMul: 0,
  role: 'Prend racine sans bouger, et son renard bondit de bad vibe en bad vibe.',
  color: '#8fd16a',
  maxHp: 170,
  speed: 6,
  radius: 14,
  pickupRadius: 110,
  attack: {
    kind: 'fox',
    damage: 20,
    cooldownTicks: 2 * TICKS_PER_BEAT,
    leaps: 2,
    falloff: 0.8,
    reach: 300,
    range: 160,
    speed: 24,
    returnSpeed: 20,
    roots: { leaps: 1, damage: 0.15, radius: 30, perNotch: 20, slowFrom: 2 },
  },
  skill: {
    id: 'hibou',
    name: 'Hibou',
    description: 'Un hibou plane au-dessus de toi et fond sur les bad vibes, qu’il fige sur place.',
    cooldownTicks: 4 * TICKS_PER_BAR,
    effect: { kind: 'hibou', bars: 1, damage: 30, frozenBeats: 1, reach: 260, speed: 30 },
  },
  gauge: { kind: 'racines', name: 'Racines', max: 4 },
  affinityWeaponIds: ['cerceaux'],
  excludedWeaponIds: ['monocycle'],
};

export const ENRACINE_UPGRADES: readonly UpgradeDefinition[] = [
  {
    id: 'bonds-de-renard',
    name: 'Bonds de renard',
    description: 'Ton renard fait un saut de plus.',
    family: 'class',
    classId: 'enracine',
    modifiers: [{ stat: 'pierceAdd', add: 1 }],
    rarities: {
      rare: {
        description: 'Ton renard fait deux sauts de plus.',
        modifiers: [{ stat: 'pierceAdd', add: 2 }],
      },
      legendary: {
        description: 'Ton renard fait trois sauts de plus.',
        modifiers: [{ stat: 'pierceAdd', add: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'racines-profondes',
    name: 'Racines profondes',
    description: 'Tes racines poussent plus vite.',
    family: 'class',
    classId: 'enracine',
    modifiers: [{ stat: 'rootsGrowthMul', mul: 4 / 3 }],
    rarities: {
      rare: {
        description: 'Tes racines poussent bien plus vite.',
        modifiers: [{ stat: 'rootsGrowthMul', mul: 1.6 }],
      },
      legendary: {
        description: 'Tes racines poussent deux fois plus vite.',
        modifiers: [{ stat: 'rootsGrowthMul', mul: 2 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'serres',
    name: 'Serres',
    description: 'Ta spéciale fond plus fort.',
    family: 'class',
    classId: 'enracine',
    modifiers: [{ stat: 'skillPowerMul', mul: 1.3 }],
    rarities: {
      rare: {
        description: 'Ta spéciale fond nettement plus fort.',
        modifiers: [{ stat: 'skillPowerMul', mul: 1.6 }],
      },
      legendary: {
        description: 'Ta spéciale fond deux fois plus fort.',
        modifiers: [{ stat: 'skillPowerMul', mul: 2 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'nuit-de-chasse',
    name: 'Nuit de chasse',
    description: 'Ta spéciale revient plus vite.',
    family: 'class',
    classId: 'enracine',
    modifiers: [{ stat: 'skillCooldownMul', mul: 0.64 }],
    rarities: {
      rare: {
        description: 'Ta spéciale revient bien plus vite.',
        modifiers: [{ stat: 'skillCooldownMul', mul: 0.36 }],
      },
      legendary: {
        description: 'Ta spéciale revient presque aussitôt.',
        modifiers: [{ stat: 'skillCooldownMul', mul: 0.16 }],
      },
    },
    maxStacks: 2,
  },
];
